import axios, { AxiosError } from "axios";
import { useQuery } from "react-query";
import { API_URL } from "../consts";
import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import IconButton from "@mui/material/IconButton";
import { visuallyHidden } from "@mui/utils";
import { format } from "date-fns";
import EditIcon from "@mui/icons-material/Edit";
import { Button, Link, MenuItem, Select, SelectChangeEvent, TextField } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Order, stableSort, getComparator } from "../ui/Tables";
import { UserState, oraPrenotaStore } from "../oraPrenotaStore";
import { memo, useCallback, useEffect, useMemo } from "react";
import { Loading } from "../ui/Loading";
import { uiStore } from "../uiStore";
import FormControl from "@mui/material/FormControl";
import * as ExcelJS from "exceljs";
import { downloadExcel } from "../utils/excel";
import { formatGiorno, readDate } from "../utils/dateUtils";

const selectOrder = (s: UserState) => s.usersOrder;
const selectSetOrder = (s: UserState) => s.setUsersOrder;

const selectOrderBy = (s: UserState) => s.usersOrderBy;
const selectSetOrderBy = (s: UserState) => s.setUsersOrderBy;

const selectUsersPage = (s: UserState) => s.usersPage;
const selectSetUsersPage = (s: UserState) => s.setUsersPage;

const selectUsersRows = (s: UserState) => s.usersRows;
const selectSetUsersRows = (s: UserState) => s.setUsersRows;

const selectStato = (s: UserState) => s.usersStato;
const selectSetStato = (s: UserState) => s.setUsersStato;

const selectFilters = (s: UserState) => s.usersFilters;
const selectSetFilters = (s: UserState) => s.setUsersFilters;

interface Data {
  userId: number;
  email: string;
  emailVera: string;
  nome: string;
  cognome: string;
  codiceSocio: string;
  indirizzo: string;
  localita: string;
  cap: string;
  provincia: string;
  mobile: string;
  dataIscrizione: Date;
  dataScadenza: Date;
  deleted: number;
  stato: number;
  dataScadenzaCertificato: Date;
}
interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
  key: string;
  filter: boolean;
  filterWidth?: number;
}

const headCells: readonly HeadCell[] = [
  {
    id: "userId",
    numeric: false,
    disablePadding: false,
    label: "",
    key: "userId",
    filter: false,
  },
  {
    id: "codiceSocio",
    numeric: false,
    disablePadding: false,
    label: "Codice",
    key: "codiceSocio",
    filter: true,
    filterWidth: 60,
  },
  {
    id: "cognome",
    numeric: false,
    disablePadding: false,
    label: "Cognome",
    key: "cognome",
    filter: true,
  },
  {
    id: "nome",
    numeric: false,
    disablePadding: false,
    label: "Nome",
    key: "nome",
    filter: true,
  },
  {
    id: "emailVera",
    numeric: false,
    disablePadding: false,
    label: "Email",
    key: "email",
    filter: true,
  },
  {
    id: "mobile",
    numeric: false,
    disablePadding: false,
    label: "Mobile",
    key: "mobile",
    filter: true,
  },
  {
    id: "dataScadenza",
    numeric: false,
    disablePadding: false,
    label: "Data Scadenza",
    key: "dataScadenza",
    filter: false,
  },
  {
    id: "stato",
    numeric: false,
    disablePadding: false,
    label: "Stato",
    key: "stato",
    filter: false,
  },
  {
    id: "dataScadenzaCertificato",
    numeric: false,
    disablePadding: false,
    label: "Scadenza Certificato",
    key: "dataScadenzaCertificato",
    filter: false,
  },
];

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  order: Order;
  orderBy: string;
  onFilter: (event: any, property: keyof Data, filter: string | number) => void;
  deleted: string;
  setDeleted: (n: string) => void;
}

export const EnhancedTableHead = memo(function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort, onFilter, deleted, setDeleted } = props;
  const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) =>
    onRequestSort(event, property);

  const createFilterHandler = (property: keyof Data) => (event: any) =>
    onFilter(event, property, event.target.value);

  const handleChange = (event: SelectChangeEvent) => setDeleted(event.target.value);

  const filters = oraPrenotaStore(selectFilters);

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.key ? headCell.key : headCell.id}>
            {headCell.filter && (
              <TextField
                label=""
                value={Object.entries(filters).find((kv) => kv[0] === headCell.id)?.[1] ?? ""}
                variant="standard"
                onChange={createFilterHandler(headCell.id)}
                sx={{ maxWidth: headCell.filterWidth ?? 300 }}
              />
            )}
            {headCell.id === "stato" && (
              <FormControl variant="standard" sx={{ width: 90 }}>
                <Select value={deleted} onChange={handleChange} label="">
                  <MenuItem value={"1"}>Tutti</MenuItem>
                  <MenuItem value={"2"}>Attivo</MenuItem>
                  <MenuItem value={"3"}>Bloccato</MenuItem>
                </Select>
              </FormControl>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
});

export function Users() {
  const { isLoading, data } = useQuery<unknown, AxiosError, { members: Data[] }>(
    ["users/getUsers"],
    async () =>
      axios.post(API_URL + "users/getUsers").then((res) => {
        const result: { members: any[] } = res.data;
        result.members.forEach((r) => {
          r.dataIscrizione = r.dataIscrizione && readDate(r.dataIscrizione);
          r.dataScadenza = r.dataScadenza && readDate(r.dataScadenza);
          r.dataScadenzaCertificato =
            r.dataScadenzaCertificato && readDate(r.dataScadenzaCertificato);
        });
        return result;
      })
  );

  const order = oraPrenotaStore(selectOrder);
  const setOrder = oraPrenotaStore(selectSetOrder);
  const orderBy = oraPrenotaStore(selectOrderBy);
  const setOrderBy = oraPrenotaStore(selectSetOrderBy);
  const page = oraPrenotaStore(selectUsersPage);
  const setPage = oraPrenotaStore(selectSetUsersPage);
  const rowsPerPage = oraPrenotaStore(selectUsersRows);
  const setRowsPerPage = oraPrenotaStore(selectSetUsersRows);
  const stato = oraPrenotaStore(selectStato);
  const setStato = oraPrenotaStore(selectSetStato);
  const filters = oraPrenotaStore(selectFilters);
  const setFilters = oraPrenotaStore(selectSetFilters);

  const dense = true;

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterRequest = (
    event: React.MouseEvent<unknown>,
    property: keyof Data,
    filter: string | number
  ) => {
    setFilters({
      ...filters,
      [property]: filter,
    });
  };

  const setTitle = uiStore((state) => state.setTitle);
  useEffect(() => setTitle("Giocatori"), [setTitle]);

  const utenti = useMemo(() => {
    if (!data?.members) return [];
    const filtered = data.members.filter(
      (r) => stato === "1" || (r.stato && stato === "2") || (!r.stato && stato === "3")
    );

    return stableSort(filtered, getComparator(order, orderBy), filters);
  }, [filters, order, orderBy, stato, data]);

  const exportExcelClick = useCallback(async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Giocatori");
    sheet.columns = [
      { header: "Codice Socio", key: "codiceSocio", width: 15 },
      { header: "Nome", key: "nome", width: 30 },
      { header: "Cognome", key: "cognome", width: 30 },
      { header: "Email", key: "emailVera", width: 30 },
      { header: "Mobile", key: "mobile", width: 20 },
      { header: "Data Scadenza", key: "dataScadenza", width: 15, style: { numFmt: "dd/mm/yyyy" } },
      { header: "Stato", key: "stato", width: 15 },
      {
        header: "Scadenza Certificato",
        key: "dataScadenzaCertificato",
        width: 15,
        style: { numFmt: "dd/mm/yyyy" },
      },
    ];

    utenti.forEach((u) =>
      sheet.addRow({
        codiceSocio: u.codiceSocio,
        cognome: u.cognome,
        nome: u.nome,
        emailVera: u.emailVera,
        mobile: u.mobile,
        dataScadenza: u.dataScadenza,
        stato: u.stato === 1 ? "Attivo" : "Bloccato",
        dataScadenzaCertificato: u.dataScadenzaCertificato,
      })
    );

    const buff = await workbook.xlsx.writeBuffer();
    downloadExcel(buff, "giocatori.xlsx");
  }, [utenti]);

  if (isLoading) return <Loading />;

  return (
    <div className="Width100">
      <Button variant="contained" color="success" onClick={exportExcelClick}>
        Excel
      </Button>
      <TableContainer>
        <Table aria-labelledby="tableTitle" size={dense ? "small" : "medium"}>
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            onFilter={handleFilterRequest}
            deleted={stato}
            setDeleted={setStato}
          />
          <TableBody classes={{ root: "table-body" }}>
            {utenti
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => (
                <RigaUtente row={row} key={row.userId} />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={utenti.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
}

export const RigaUtente = memo(function RigaUtente(props: { row: Data }) {
  const { row } = props;
  return (
    <TableRow hover tabIndex={-1}>
      <TableCell>
        <Link to={"/app/editUser/" + row.userId} underline="none" component={RouterLink}>
          <IconButton aria-label="edit" sx={{ p: 0 }}>
            <EditIcon />
          </IconButton>
        </Link>
      </TableCell>

      <TableCell>{row.codiceSocio}</TableCell>
      <TableCell>{row.cognome}</TableCell>
      <TableCell>{row.nome}</TableCell>
      <TableCell>{row.emailVera}</TableCell>
      <TableCell>{row.mobile}</TableCell>
      <TableCell>{formatGiorno(row.dataScadenza)}</TableCell>
      <TableCell>{row.stato === 1 ? "Attivo" : "Bloccato"}</TableCell>
      <TableCell>{formatGiorno(row.dataScadenzaCertificato)}</TableCell>
    </TableRow>
  );
});
